import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { MessageCircle, Plus, RefreshCw, QrCode, Unplug, Loader2, FolderSync } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import {
  useZaloAccounts,
  useConnectZalo,
  useZaloLogin,
  useZaloReconnect,
  useDeleteZaloAccount,
  useSyncZaloDirectory,
  type ChannelAccount,
} from '@/hooks/use-integrations'
import {
  SelectableTable,
  accountColumns,
  accountSearchText,
  ACCOUNT_FILTERS,
  BulkDisconnectButton,
} from './accounts-table'
import { noConnectHint, useAutoAdd } from './channel-catalog'
import { useMyPermissions } from '@/hooks/use-settings'
import { useOnboardingReturn } from '@/hooks/use-onboarding-return'
import { QrLoginDialog } from './qr-login-dialog'
import { BackfillAccountButton, BackfillProgressLine } from './backfill-account-button'
import { ZaloQuotaLine } from './zalo-quota-line'
import { useQuotaExceededDialog } from '@/components/billing/quota-exceeded-dialog'

export function ZaloPersonalTab() {
  const { t } = useTranslation('chanZalo')
  const onboardingReturn = useOnboardingReturn()
  const { data, isLoading, isError } = useZaloAccounts('personal')
  const connect = useConnectZalo()
  const login = useZaloLogin()
  const reconnect = useZaloReconnect()
  const remove = useDeleteZaloAccount()
  const syncDirectory = useSyncZaloDirectory()
  // Ẩn nút theo đúng quyền backend: kết nối mới = create, đăng nhập/kết nối lại = update, ngắt = delete.
  const { data: perms } = useMyPermissions()
  const canCreate = !!perms?.has('integrations.create')
  const canUpdate = !!perms?.has('integrations.update')
  const canDelete = !!perms?.has('integrations.delete')

  const [qrAccountId, setQrAccountId] = useState<string | null>(null)
  const [qrOpen, setQrOpen] = useState(false)
  const [toDelete, setToDelete] = useState<ChannelAccount | null>(null)
  const [syncingId, setSyncingId] = useState<string | null>(null)
  const quotaDialog = useQuotaExceededDialog()

  const openQr = (accountId: string) => {
    setQrAccountId(accountId)
    setQrOpen(true)
  }

  const handleConnect = () => {
    connect.mutate(undefined, {
      onSuccess: (res) => openQr(res.accountId),
      onError: (e) => { if (!quotaDialog.handle(e)) toast.error(apiError(e)) },
    })
  }

  useAutoAdd(handleConnect)

  const handleRelogin = (acc: ChannelAccount) => {
    login.mutate(acc.id, {
      onSuccess: () => openQr(acc.id),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  const handleReconnect = (acc: ChannelAccount) => {
    reconnect.mutate(acc.id, {
      onSuccess: () => toast.success(t('personal.reconnecting')),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  /**
   * Kéo lại bạn bè + nhóm. Có thể mất vài chục giây với tài khoản nhiều bạn nên
   * khoá riêng thẻ đang chạy, các thẻ khác vẫn bấm được.
   */
  const handleSync = (acc: ChannelAccount) => {
    setSyncingId(acc.id)
    syncDirectory.mutate(acc.id, {
      onSuccess: (r) => {
        toast.success(r.message)
        if (r.truncated) {
          toast.warning(t('personal.syncTruncated'))
        }
      },
      onError: (e) => toast.error(apiError(e)),
      onSettled: () => setSyncingId(null),
    })
  }

  const handleDelete = () => {
    if (!toDelete) return
    remove.mutate(toDelete.id, {
      onSuccess: () => {
        toast.success(t('personal.disconnected'))
        setToDelete(null)
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('personal.intro')}
        </p>
        {canCreate && (
          <Button onClick={handleConnect} disabled={connect.isPending}>
            {connect.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Plus />
            )}
            {t('common:actions.connect')}
          </Button>
        )}
      </div>

      <SelectableTable
        rows={data}
        rowKey={(a) => a.id}
        loading={isLoading}
        error={isError}
        filters={ACCOUNT_FILTERS}
        searchText={accountSearchText}
        empty={
          <EmptyState
            icon={MessageCircle}
            title={t('personal.emptyTitle')}
            description={
              canCreate ? t('personal.emptyDesc') : noConnectHint()
            }
          />
        }
        columns={accountColumns({
          fallbackName: t('personal.fallbackName'),
          sub: (acc) => acc.phone || acc.externalUid || undefined,
          actions: (acc) => {
            const isConnected = acc.liveStatus === 'connected' && !acc.isDisabled
            return (
              <>
                {!isConnected && canUpdate && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReconnect(acc)}
                      disabled={reconnect.isPending}
                      title={t('personal.reconnectTitle')}
                    >
                      <RefreshCw /> {t('common:actions.reconnect')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRelogin(acc)}
                      disabled={login.isPending}
                      title={t('personal.reloginTitle')}
                    >
                      <QrCode />
                    </Button>
                  </>
                )}
                {isConnected && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSync(acc)}
                      disabled={syncingId === acc.id}
                      title={t('personal.syncTitle')}
                    >
                      {syncingId === acc.id ? <Loader2 className="animate-spin" /> : <FolderSync />}
                      {syncingId === acc.id ? t('personal.syncing') : t('personal.sync')}
                    </Button>
                    {/* Kéo lịch sử tin nhắn cũ — cùng dòng với Đồng bộ. */}
                    <BackfillAccountButton accountId={acc.id} />
                  </>
                )}
                {canDelete && (
                  <Button variant="ghost" size="sm" onClick={() => setToDelete(acc)} title={t('common:actions.disconnect')}>
                    <Unplug />
                  </Button>
                )}
              </>
            )
          },
        })}
        renderDetail={(acc) => (
          <div className="space-y-1 text-xs">
            <BackfillProgressLine accountId={acc.id} />
            {acc.liveStatus === 'kicked' && (
              <p className="text-destructive">
                {t('personal.kicked')}
              </p>
            )}
            <ZaloQuotaLine accountId={acc.id} />
          </div>
        )}
        bulkActions={(selected, clear) => (
          <BulkDisconnectButton
            selected={selected}
            onDone={clear}
            description={t('personal.disconnectDesc')}
          />
        )}
      />

      <QrLoginDialog
        accountId={qrAccountId}
        open={qrOpen}
        onOpenChange={setQrOpen}
        onConnected={() => onboardingReturn.complete(1)}
      />
      {quotaDialog.node}

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('personal.disconnectTitle')}</DialogTitle>
            <DialogDescription>
              {t('personal.disconnectDesc')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDelete(null)}>
              {t('personal.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={remove.isPending}>
              {remove.isPending && <Loader2 className="animate-spin" />}
              {t('common:actions.disconnect')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
